import type { BadgeTone } from '../../components/primitives/AppBadge/badge-types'
import { CONTENT_STATUSES, type ContentStatus } from '../../types/content'

/** Reads a post's stored status (kebab-case, matching the server's `PostStatus`), falling back to `draft`. */
export function toContentStatus(value: unknown): ContentStatus {
  if (typeof value !== 'string') return 'draft'
  if ((CONTENT_STATUSES as readonly string[]).includes(value)) return value as ContentStatus
  return 'draft'
}

/** Badge tone and i18n label key for each {@link ContentStatus}, shared by every surface that shows one. */
export const STATUS_BADGE: Record<ContentStatus, { tone: BadgeTone; labelKey: string }> = {
  draft: { tone: 'neutral', labelKey: 'content.status.draft' },
  'pending-handoff': { tone: 'blue', labelKey: 'content.status.pendingHandoff' },
  scheduled: { tone: 'blue', labelKey: 'content.status.scheduled' },
  published: { tone: 'orange', labelKey: 'content.status.published' },
  failed: { tone: 'red', labelKey: 'content.status.failed' },
}

/** Every action that moves a post off its current {@link ContentStatus}. */
export const CONTENT_TRANSITIONS = [
  'schedule',
  'publishNow',
  'reschedule',
  'unschedule',
  'retry',
] as const

export type ContentTransition = (typeof CONTENT_TRANSITIONS)[number]

/** The legal transitions out of each {@link ContentStatus}, main action first; every surface that offers status actions reads this one table. */
export const STATUS_TRANSITIONS: Record<ContentStatus, readonly ContentTransition[]> = {
  draft: ['schedule', 'publishNow'],
  'pending-handoff': ['reschedule', 'unschedule'],
  scheduled: ['reschedule', 'unschedule'],
  published: [],
  failed: ['retry', 'publishNow'],
}

/** The button emphasis and i18n label key each {@link ContentTransition} shows with, on every surface. */
export const TRANSITION_CONTROL: Record<
  ContentTransition,
  { variant: 'primary' | 'secondary'; labelKey: string }
> = {
  schedule: { variant: 'primary', labelKey: 'content.schedule' },
  publishNow: { variant: 'secondary', labelKey: 'content.publishNow' },
  reschedule: { variant: 'secondary', labelKey: 'content.reschedule' },
  unschedule: { variant: 'secondary', labelKey: 'content.unschedule' },
  retry: { variant: 'primary', labelKey: 'content.tryAgain' },
}

/** Whether a post in this status may have a design attached; mirrors the server's refusal once a post is scheduled, awaiting handoff, or published. */
export function canAttachDesign(status: ContentStatus): boolean {
  return status === 'draft' || status === 'failed'
}
